import React from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { SapMetricsSummary } from '@/types/domain/sap-simulator';
import { Database, Layers, ShoppingBag, AlertTriangle, ShieldCheck } from 'lucide-react';

interface SapDashboardMetricsHeaderProps {
  metrics: SapMetricsSummary;
}

export const SapDashboardMetricsHeader: React.FC<SapDashboardMetricsHeaderProps> = ({ metrics }) => {
  const cards = [
    {
      title: 'Material Master',
      value: metrics.totalMaterials,
      subtitle: 'Active SAP materials',
      icon: Database,
      colorClass: 'text-primary bg-primary/10 border-primary/20',
      badge: 'Master',
    },
    {
      title: 'Vendor Master',
      value: metrics.totalVendors,
      subtitle: 'Registered vendors',
      icon: Database,
      colorClass: 'text-blue-600 bg-blue-500/10 border-blue-500/20',
      badge: 'Master',
    },
    {
      title: 'Purchase Requisitions',
      value: metrics.totalPrs,
      subtitle: 'ME51N PR records',
      icon: Layers,
      colorClass: 'text-purple-600 bg-purple-500/10 border-purple-500/20',
      badge: 'ME51N',
    },
    {
      title: 'Purchase Orders',
      value: metrics.totalPos,
      subtitle: 'ME21N PO records',
      icon: ShoppingBag,
      colorClass: 'text-emerald-600 bg-emerald-500/10 border-emerald-500/20',
      badge: 'ME21N',
    },
    {
      title: 'Integration Success',
      value: `${metrics.successRatePercentage}%`,
      subtitle: `${metrics.totalApiCalls} total API calls`,
      icon: ShieldCheck,
      colorClass: 'text-emerald-700 bg-emerald-500/10 border-emerald-500/20',
      badge: 'Gateway',
    },
    {
      title: 'Failed & Retry Queue',
      value: metrics.failedIntegrations + metrics.retryQueueCount,
      subtitle: `${metrics.failedIntegrations} failed • ${metrics.retryQueueCount} in retry queue`,
      icon: AlertTriangle,
      colorClass: 'text-rose-600 bg-rose-500/10 border-rose-500/20',
      badge: 'Queue',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5 font-heading">
      {cards.map((card) => {
        const Icon = card.icon;

        return (
          <Card key={card.title} className="rounded-2xl border border-border bg-card shadow-xs">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className={`p-2 rounded-xl border ${card.colorClass}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <span className="text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-muted text-foreground-secondary">
                  {card.badge}
                </span>
              </div>

              <div>
                <span className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight text-foreground block truncate">
                  {card.value}
                </span>
                <span className="text-xs font-bold text-foreground leading-tight block truncate">
                  {card.title}
                </span>
              </div>

              <p className="text-[10px] text-foreground-muted font-medium truncate pt-1 border-t border-border/40">
                {card.subtitle}
              </p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
